/**
 * @fileoverview Virtual Race Results List (activity table, filters, pagination)
 * @requires virtual-race-utility.js
 */

(function() {
    'use strict';

    if (typeof window.VirtualRaceUtils === 'undefined') {
        console.error('VirtualRaceUtils not loaded');
        return;
    }

    const { CONFIG, SecurityUtils, TeamColorManager, TeamLabelManager, BaseAPIClient, DOMHelpers, UrlParams, StorageManager, BaseCustomElement } = window.VirtualRaceUtils;
    const LOCAL_CONFIG = {
        DEFAULT_COLOR: '#003B6E',
        DEFAULT_UNIT: 'miles',
        LOADING_DELAY: 500,
        DEBOUNCE_DELAY: 300,
        TEAM_FILTER_DEBOUNCE_MS: 150
    };

    function isRecoverableListError(error) {
        if (!error) return true;
        if (error.name === 'AbortError') return true;
        if (typeof error.message === 'string' && error.message.includes('Rate limit')) return true;
        return false;
    }

    Object.assign(CONFIG, LOCAL_CONFIG);
    const ACTIVITY_ICONS = {
        run: 'https://cdn.shopify.com/s/files/1/0672/0834/7840/files/run.png?v=1743765050',
        jog: 'https://cdn.shopify.com/s/files/1/0672/0834/7840/files/jog.png?v=1743765050',
        walk: 'https://cdn.shopify.com/s/files/1/0672/0834/7840/files/walk.png?v=1743765050',
        bike: 'https://cdn.shopify.com/s/files/1/0672/0834/7840/files/bike.png?v=1743765050',
        hike: 'https://cdn.shopify.com/s/files/1/0672/0834/7840/files/hike.png?v=1743765050',
        swim: 'https://cdn.shopify.com/s/files/1/0672/0834/7840/files/swim.png?v=1743765050',
        other: 'https://cdn.shopify.com/s/files/1/0672/0834/7840/files/other.png?v=1743765050',
        multiple: 'https://cdn.shopify.com/s/files/1/0672/0834/7840/files/multi.png?v=1743765050',
        default: 'https://cdn.shopify.com/s/files/1/0672/0834/7840/files/run.png?v=1743765050'
    };

   /**
    *  Activity Icon Manager
    */
    class ActivityIconManager {

        getIcon(activityType) {
            if (!activityType || typeof activityType !== 'string') {
                return ACTIVITY_ICONS.default;
            }

            const normalized = activityType.toLowerCase().trim();

            if (normalized === 'multiple_activity') {
                return ACTIVITY_ICONS.multiple;
            }

            return ACTIVITY_ICONS[normalized] || ACTIVITY_ICONS.default;
        }

        isValidIconUrl(url) {
            return SecurityUtils.validateUrl(url) &&
                   url.startsWith('https://cdn.shopify.com/');
        }
    }

    /**
     *  HTML Builder
     */

    class HTMLBuilder {

        createNameCell(activity, iconManager) {
            const td = document.createElement('td');
            td.setAttribute('label', 'Name');
            td.className = 'name-row';

            const container = document.createElement('div');
            container.className = 'individual-virtual-race-display-name-container';

            const nameContainer = document.createElement('span');
            nameContainer.className = 'name-activity-container';

            const iconContainer = document.createElement('span');
            iconContainer.className = 'activity-icon-container';

            const img = document.createElement('img');
            img.width = 35;
            img.height = 35;
            img.className = 'activity-icon';
            img.src = iconManager.getIcon(activity.activity_type);
            img.alt = 'Activity Icon';

            iconContainer.appendChild(img);

            const nameSpan = document.createElement('span');
            nameSpan.textContent = SecurityUtils.sanitizeText(activity.name || '');

            nameContainer.appendChild(iconContainer);
            nameContainer.appendChild(nameSpan);

            const iconsContainer = this._createIconsContainer(activity);

            container.appendChild(nameContainer);
            container.appendChild(iconsContainer);
            td.appendChild(container);

            return td;
        }

        _createIconsContainer(activity) {
            const container = document.createElement('span');
            container.className = 'icons-container';
            if (activity.note) {
                const noteSpan = document.createElement('span');
                noteSpan.className = 'tip-popup cursor-pointer';
                noteSpan.setAttribute('is', 'tip-popover');

                const noteImg = document.createElement('img');
                noteImg.className = 'tip-icon';
                noteImg.src = window.virtualRace?.icon_chat || '';
                noteImg.alt = 'Feedback icon';

                const noteContent = document.createElement('span');
                noteContent.className = 'tip-content';
                noteContent.textContent = SecurityUtils.sanitizeText(activity.note);

                noteSpan.appendChild(noteImg);
                noteSpan.appendChild(noteContent);
                container.appendChild(noteSpan);
            }
            if (activity.personalBest && window.virtualRace?.icon_medal) {
                const medalSpan = document.createElement('span');
                medalSpan.className = 'tip-popup cursor-pointer';
                medalSpan.setAttribute('is', 'tip-popover');

                const medalImg = document.createElement('img');
                medalImg.className = 'tip-icon';
                medalImg.src = window.virtualRace.icon_medal;
                medalImg.alt = 'Medal icon';

                const medalContent = document.createElement('span');
                medalContent.className = 'tip-content';
                medalContent.textContent = `Congratulations to ${SecurityUtils.sanitizeText(activity.name)} for achieving a personal record running this race!`;

                medalSpan.appendChild(medalImg);
                medalSpan.appendChild(medalContent);
                container.appendChild(medalSpan);
            }
            if (activity.raceCount && activity.raceCount > 1) {
                const countDiv = document.createElement('div');
                countDiv.className = 'd-flex flex-column align-items-center';

                const countP = document.createElement('p');
                countP.className = 'mb-0';
                countP.textContent = String(activity.raceCount);

                const labelP = document.createElement('p');
                labelP.className = 'mb-0';
                labelP.textContent = 'Entries';

                countDiv.appendChild(countP);
                countDiv.appendChild(labelP);
                container.appendChild(countDiv);
            }

            return container;
        }

        createDateCell(startDate, endDate) {
            const td = document.createElement('td');
            td.setAttribute('label', 'Race Date');

            const dateObj = new Date(startDate);
            const formattedStart = dateObj.toLocaleDateString('en-US', {
                timeZone: 'UTC',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });

            td.setAttribute('data-order', formattedStart);

            const container = document.createElement('div');
            container.className = 'race-date-container';

            let dateText = formattedStart;

            if (endDate) {
                const endDateObj = new Date(endDate);
                const formattedEnd = endDateObj.toLocaleDateString('en-US', {
                    timeZone: 'UTC',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                });
                dateText += ` - ${formattedEnd}`;
            }

            container.textContent = dateText;
            td.appendChild(container);

            return td;
        }

        createDataCell(label, value, className = '') {
            const td = document.createElement('td');
            td.setAttribute('label', label);
            if (className) td.className = className;

            const container = document.createElement('div');
            container.className = `${label.toLowerCase().replace(/\s+/g, '-')}-container`;
            container.textContent = SecurityUtils.sanitizeText(String(value));

            td.appendChild(container);

            return td;
        }

        createTeamCell(teamName, teamColor, isTeamProduct) {
            const td = document.createElement('td');
            td.setAttribute('label', 'Team');
            td.className = `team-cell text-capitalize`;
            td.setAttribute('data-teamname', SecurityUtils.sanitizeText(teamName || ''));
            
            const teamLabel = TeamLabelManager.getTeamLabelSync(teamName);
            td.textContent = SecurityUtils.sanitizeText(teamLabel || '');

            if (!isTeamProduct) {
                td.classList.add('d-none');
            }

            const color = teamColor || CONFIG.DEFAULT_COLOR;
            td.style.setProperty('--team-color', color);

            return td;
        }
    }

    /**
     *  Activity API Client
     */

    class ActivityAPIClient extends BaseAPIClient {

        async fetchActivityList(payload) {
            if (!SecurityUtils.validateProductId(payload.product_id)) {
                throw new Error('Invalid product ID');
            }

            this.cancelPendingRequests();
            this._abortController = new AbortController();

            return await this._executeWithRetry(async () => {
                const response = await this._fetchWithTimeout('/apps/proxy/virtual-race/list', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload),
                    signal: this._abortController.signal
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const data = await response.json();

                if (data.statusCode !== 200) {
                    throw new Error('Invalid response status');
                }
                if (!SecurityUtils.validateActivityList(data)) {
                    throw new Error('Invalid activity list data structure');
                }

                return data;
            });
        }
    }

    class ActivityListUtils {
        static getAgeRange(age) {
            if (!SecurityUtils.validateAge(age)) {
                return 'Invalid Age';
            }
            const lowerBound = Math.floor(age / 10) * 10;
            const upperBound = lowerBound + 9;
            return `${lowerBound}-${upperBound}`;
        }
    }

    /**
     *  Activity list state (used by VirtualRaceActivityListElement custom element)
     */

    window.activityComponent = function activityComponent() {
        const apiClient = new ActivityAPIClient();
        const iconManager = new ActivityIconManager();
        const htmlBuilder = new HTMLBuilder();
        const teamColorManager = new TeamColorManager();

        return {
            // State properties
            activities: [],
            isteam: false,
            isLoading: true,
            isFirstLoad: true,
            selectedColor: CONFIG.DEFAULT_COLOR,
            currentPage: 1,
            maxPageReached: 1,
            distanceUnit: CONFIG.DEFAULT_UNIT,
            page: {
                has_next: false,
                has_previous: false,
                next: null,
                previous: null
            },
            payload: {
                filters: {
                    gender: null,
                    age: null,
                    team: null
                },
                page: 1,
                sort_by: 'lastRunningDate',
                sort_order: 'desc',
                search: null,
                product_id: null
            },
            _teamFilterDebounceId: null,
            _pendingTeamValue: null,
            _documentHandlers: null,

            async initComponent() {
                await this.initExternalEvents();
                this.isLoading = true;
                document.dispatchEvent(new CustomEvent('virtual-race-activity-list-loading', { detail: { loading: true } }));
                
                try {
                    await this.waitForProductData();
                    await this.getProductAndTeamData();
                    await this.renderProductData();
                    
                    if (this.isFirstLoad) {
                        this.buildSkeletonRows();
                    }
                    
                    try {
                        await this.getRaceData();
                        await this.buildTable();
                    } catch (error) {
                        this.activities = [];
                        await this.buildTable();
                        if (!isRecoverableListError(error)) {
                            console.error('Race data fetch error:', error.name);
                            alert('Unable to load activity data. Please try again later.');
                        }
                    }
                } finally {
                    this.isFirstLoad = false;
                    this.isLoading = false;
                    document.dispatchEvent(new CustomEvent('virtual-race-activity-list-updated'));
                }
            },

            async waitForProductData() {
                if (typeof window.isVirtualRaceTeam !== 'undefined') {
                    return;
                }
                
                return new Promise((resolve) => {
                    const timeout = setTimeout(() => {
                        resolve();
                    }, 1000);
                    
                    const handler = () => {
                        clearTimeout(timeout);
                        document.removeEventListener('updateIsTeam', handler);
                        resolve();
                    };
                    
                    document.addEventListener('updateIsTeam', handler);
                });
            },

            async initExternalEvents() {
                this._documentHandlers = [];
                const addDoc = (event, handler) => {
                    document.addEventListener(event, handler);
                    this._documentHandlers.push({ event, handler });
                };

                addDoc('updateIsTeam', (e) => {
                    this.isteam = e.detail?.isteam;
                });

                addDoc('updateCurrentUnit', async (e) => {
                    this.isLoading = true;
                    this.distanceUnit = e.detail?.unit;
                    await this.buildTable();
                    setTimeout(() => {
                        this.isLoading = false;
                    }, CONFIG.LOADING_DELAY);
                });

                addDoc('updateGenderFilter', async (e) => {
                    this.isLoading = true;
                    await this.updateGenderFilter(e.detail?.gender);
                });

                addDoc('updateAgeFilter', async (e) => {
                    this.isLoading = true;
                    await this.updateAgeFilter(e.detail?.age);
                });

                addDoc('updateTeamFilter', (e) => {
                    this._pendingTeamValue = e.detail?.team;
                    if (this._teamFilterDebounceId) clearTimeout(this._teamFilterDebounceId);
                    this._teamFilterDebounceId = setTimeout(async () => {
                        this._teamFilterDebounceId = null;
                        const value = this._pendingTeamValue;
                        this._pendingTeamValue = null;
                        if (value === undefined) return;
                        this.isLoading = true;
                        await this.updateTeamFilter(value);
                    }, CONFIG.TEAM_FILTER_DEBOUNCE_MS);
                });

                addDoc('updateSearch', async (e) => {
                    this.isLoading = true;
                    await this.updateSearch(e.detail?.search);
                });
            },

            async getProductAndTeamData() {
                if (typeof window.isVirtualRaceTeam !== 'undefined') {
                    this.isteam = window.isVirtualRaceTeam;
                }
            },

            async updateSearch(search) {
                this.isLoading = true;
                this.payload.search = SecurityUtils.sanitizeSearch(search) || null;
                this.payload.page = 1;
                this.currentPage = 1;
                await this.updateTable();
            },

            async renderProductData() {
                const vrId = UrlParams.getVrId();
                if (vrId) {
                    this.payload.product_id = vrId;
                    StorageManager.setSelectedProductId(vrId);
                }
            },

            async getRaceData() {
                try {
                    const data = await apiClient.fetchActivityList(this.payload);
                    const response = data?.response;
                    const page = response?.page;

                    if (data?.statusCode === 200 && response) {
                        this.activities = Array.isArray(response.data) ? response.data : [];
                        this.page = page || { has_next: false, has_previous: false, next: null, previous: null };
                        if (page?.has_next === false) {
                            this.maxPageReached = this.currentPage;
                        } else {
                            this.maxPageReached = this.currentPage + 1;
                        }
                        
                        this._initializeTeamColors();
                    } else {
                        this.activities = [];
                    }
                } catch (error) {
                    if (error.name === 'AbortError') {
                        return;
                    }
                    throw error;
                }
            },

            _initializeTeamColors() {
                if (!this.isteam || !Array.isArray(this.activities)) return;
                
                const uniqueTeams = [...new Set(this.activities
                    .map(activity => activity.team || activity.teamName)
                    .filter(team => team && String(team).toLowerCase() !== 'n/a')
                )];
                
                uniqueTeams.forEach(teamName => {
                    teamColorManager.getColorForTeam(teamName);
                });
                
                window.teamColorDictionary = teamColorManager.getAllColors();
            },

            async buildTable() {
                this.buildRow();
            },

            async setTable(column, event, columnIndex) {
                let dataAttrValue = null;
                let sortType = null;
                let targetElement = null;

                if (event.target && event.target.tagName) {
                    const tagName = event.target.tagName.toLowerCase();

                    if (tagName === 'span') {
                        const th = event.target.closest('th');
                        if (th) {
                            dataAttrValue = th.getAttribute('dataAttributr');
                            sortType = th.getAttribute('data-sorttype');
                            targetElement = th;
                        }
                    } else if (tagName === 'th') {
                        dataAttrValue = event.target.getAttribute('dataAttributr');
                        sortType = event.target.getAttribute('data-sorttype');
                        targetElement = event.target;
                    }
                }

                if (!dataAttrValue || !targetElement) return;

                this.payload.sort_by = SecurityUtils.sanitizeText(dataAttrValue);

                // Update sort order
                document.querySelectorAll('.customTHClass').forEach(th => {
                    th.classList.remove('dt-ordering-asc', 'dt-ordering-desc');
                    th.setAttribute('aria-sort', 'none');
                    th.setAttribute('data-sorttype', 'none');
                });

                if (sortType === 'none') {
                    this.payload.sort_order = 'asc';
                    targetElement.setAttribute('aria-sort', 'ascending');
                    targetElement.setAttribute('data-sorttype', 'asc');
                    targetElement.classList.add('dt-ordering-asc');
                } else if (sortType === 'asc') {
                    this.payload.sort_order = 'desc';
                    targetElement.setAttribute('aria-sort', 'descending');
                    targetElement.setAttribute('data-sorttype', 'desc');
                    targetElement.classList.add('dt-ordering-desc');
                } else {
                    this.payload.sort_order = 'asc';
                    targetElement.setAttribute('aria-sort', 'ascending');
                    targetElement.setAttribute('data-sorttype', 'asc');
                    targetElement.classList.add('dt-ordering-asc');
                }

                await this.updateTable();
            },

            async updateTable() {
                this.isLoading = true;
                document.dispatchEvent(new CustomEvent('virtual-race-activity-list-loading', { detail: { loading: true } }));

                try {
                    await this.getRaceData();
                    await this.buildTable();
                } catch (error) {
                    this.activities = [];
                    await this.buildTable();
                    if (!isRecoverableListError(error)) {
                        console.error('Race data fetch error:', error.name);
                        alert('Unable to load activity data. Please try again later.');
                    }
                } finally {
                    await new Promise(r => setTimeout(r, CONFIG.LOADING_DELAY));
                    this.isLoading = false;
                    document.dispatchEvent(new CustomEvent('virtual-race-activity-list-updated'));
                }
            },

            buildSkeletonRows() {
                const tbody = document.querySelector('#individual-virtual-race-time-table tbody');
                if (!tbody) return;

                tbody.innerHTML = '';

                for (let i = 0; i < 10; i++) {
                    const tr = document.createElement('tr');
                    tr.className = 'skeleton-row';

                    tr.appendChild(this._createSkeletonCell('Name', '<div class="skeleton-cell w-100"></div>'));
                    tr.appendChild(this._createSkeletonCell('Race Date', '<div class="skeleton-cell w-100"></div>'));
                    tr.appendChild(this._createSkeletonCell('Race Time', '<div class="skeleton-cell w-100"></div>'));
                    tr.appendChild(this._createSkeletonCell('Race Pace', '<div class="skeleton-cell w-100"></div>'));
                    tr.appendChild(this._createSkeletonCell('Race Distance', '<div class="skeleton-cell w-100"></div>'));

                    const teamCell = document.createElement('td');
                    teamCell.setAttribute('label', 'Team');
                    teamCell.className = 'team-cell text-capitalize';
                    if (!this.isteam) {
                        teamCell.classList.add('d-none');
                    }
                    teamCell.innerHTML = '<div class="skeleton-cell w-80"></div>';
                    tr.appendChild(teamCell);

                    tbody.appendChild(tr);
                }
            },

            _createSkeletonCell(label, innerHTML, className = '') {
                const td = document.createElement('td');
                td.setAttribute('label', label);
                if (className) td.className = className;
                td.innerHTML = innerHTML;
                return td;
            },

            buildRow() {
                const tbody = document.querySelector('#individual-virtual-race-time-table tbody');
                if (!tbody) return;

                tbody.innerHTML = '';

                if (this.activities.length === 0) {
                    const tr = document.createElement('tr');
                    const td = document.createElement('td');
                    td.setAttribute('colspan', '100%');
                    td.className = 'dt-empty';
                    td.textContent = 'No activities found';
                    tr.appendChild(td);
                    tbody.appendChild(tr);
                    return;
                }

                this.activities.forEach(activity => {
                    if (!SecurityUtils.validateActivity(activity)) return;

                    const tr = document.createElement('tr');

                    tr.appendChild(htmlBuilder.createNameCell(activity, iconManager));
                    tr.appendChild(htmlBuilder.createDateCell(activity.startDate, activity.endDate));
                    tr.appendChild(htmlBuilder.createDataCell('Race Time', activity.time || ''));
                    const paceValue = this.distanceUnit === 'miles'
                        ? activity.pace?.miles || ''
                        : activity.pace?.km || '';
                    tr.appendChild(htmlBuilder.createDataCell('Race Pace', paceValue, 'hide-if-empty'));
                    const hiddenPaceCell = htmlBuilder.createDataCell('Race Pace', activity.pace?.km || '', 'd-none');
                    tr.appendChild(hiddenPaceCell);
                    const distanceValue = this.distanceUnit === 'miles'
                        ? `${activity.distances?.miles || ''} miles`
                        : `${activity.distances?.km || ''} km`;
                    tr.appendChild(htmlBuilder.createDataCell('Race Distance', distanceValue, 'hide-if-empty'));
                    const hiddenDistCell = htmlBuilder.createDataCell('Race Distance', `${activity.distances?.km || ''} km`, 'd-none');
                    tr.appendChild(hiddenDistCell);
                    tr.appendChild(htmlBuilder.createDataCell('Gender', activity.gender || '', 'hide-if-empty d-none'));
                    const ageRange = ActivityListUtils.getAgeRange(parseInt(activity.age));
                    tr.appendChild(htmlBuilder.createDataCell('Age Range', ageRange, 'hide-if-empty d-none'));
                    const teamColor = TeamColorManager.getTeamColorFromGlobal(activity.team);
                    tr.appendChild(htmlBuilder.createTeamCell(activity.team, teamColor, this.isteam));

                    tbody.appendChild(tr);
                });
            },

            async loadPageIndex(index) {
                if (index === this.currentPage) return;

                this.isLoading = true;
                this.currentPage = index;
                this.payload.page = index;
                await this.updateTable();
            },

            async loadPreviousPage() {
                if (!this.page.has_previous) return;

                this.isLoading = true;
                this.payload.page = this.currentPage - 1;
                this.currentPage = this.payload.page;
                await this.updateTable();
            },

            async loadNextPage() {
                if (!this.page.has_next) return;

                this.isLoading = true;
                this.payload.page = this.currentPage + 1;
                this.currentPage = this.payload.page;
                await this.updateTable();
            },

            async _updateFilter(filterName, value) {
                this.isLoading = true;
                this.payload.filters[filterName] = value === 'all' ? null : SecurityUtils.sanitizeText(value);
                this.payload.page = 1;
                this.currentPage = 1;
                await this.updateTable();
            },

            async updateGenderFilter(value) {
                return this._updateFilter('gender', value);
            },

            async updateAgeFilter(value) {
                return this._updateFilter('age', value);
            },

            async updateTeamFilter(value) {
                return this._updateFilter('team', value);
            },

            destroy() {
                if (this._teamFilterDebounceId) {
                    clearTimeout(this._teamFilterDebounceId);
                    this._teamFilterDebounceId = null;
                }
                if (this._documentHandlers?.length) {
                    this._documentHandlers.forEach(({ event, handler }) => {
                        document.removeEventListener(event, handler);
                    });
                    this._documentHandlers = null;
                }
                apiClient.cancelPendingRequests();
            }
        };
    };

    // ============================================================================
    // IMAGE GALLERY COMPONENT
    // ============================================================================

    window.fetchImagegallery = function fetchImagegallery(productId) {
        return {
            items: [],
            isLoading: false,

            async getData() {
                this.isLoading = true;

                if (!productId || !SecurityUtils.validateProductId(productId)) {
                    this.isLoading = false;
                    return;
                }

                const query = `
                    query productQuery {
                        product(id: "gid://shopify/Product/${productId}") {
                            metafield(key: "photo_container", namespace: "custom") {
                                references(first: 50) {
                                    edges {
                                        node {
                                            ... on MediaImage {
                                                id
                                                image {
                                                    url
                                                    altText
                                                    width
                                                    height
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                `;

                try {
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), CONFIG.API_TIMEOUT);

                    const response = await fetch(
                        `${window.globalSpace?.apiDomain || ''}/api/2023-01/graphql.json`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-Shopify-Storefront-Access-Token': '382d0c774e95be1f0d7c90e6c08bba49'
                            },
                            body: JSON.stringify({ query }),
                            signal: controller.signal
                        }
                    );

                    clearTimeout(timeoutId);

                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}`);
                    }

                    const res = await response.json();

                    if (!res.data?.product) {
                        console.log('No product found');
                        this.items = [];
                        return;
                    }

                    const metafield = res.data.product.metafield;
                    if (!metafield || !metafield.references?.edges) {
                        console.log('No images found in metafield');
                        this.items = [];
                        return;
                    }

                    this.items = metafield.references.edges
                        .filter(edge => edge?.node?.image)
                        .map(edge => ({
                            url: `${edge.node.image.url}&width=440`,
                            alt: SecurityUtils.sanitizeText(edge.node.image.altText || 'Shopify Image'),
                            width: 220,
                            height: 220
                        }));
                } catch (error) {
                    console.error('Image gallery fetch error:', error.name);
                    this.items = [];
                } finally {
                    this.isLoading = false;
                }
            }
        };
    };

    // ============================================================================
    // CUSTOM ELEMENT: virtual-race-activity-list
    // ============================================================================

    class VirtualRaceActivityListElement extends BaseCustomElement {

        async _init() {
            this._state = window.activityComponent();
            this._setupEventListeners();
            
            await TeamLabelManager._ensureTeamsLoaded();
            
            this._render();

            await this._state.initComponent();
        }

        _setupEventListeners() {
            this._addEventListener(this, 'click', this._handleClick.bind(this));
            this._addEventListener(document, 'updateIsTeam', () => {
                if (this._state) this._render();
            });
            const onListLoadingOrUpdated = (e) => {
                console.log('onListLoadingOrUpdated', e.type, e.detail);
                if (this._state) this._render();
                const loading = e?.type === 'virtual-race-activity-list-loading' && e?.detail?.loading === true;
                const row = this.closest('.row');
                if (row) {
                    row.classList.toggle('vr-results-loading', loading);
                    row.setAttribute('aria-busy', loading ? 'true' : 'false');
                }
            };
            this._addEventListener(document, 'virtual-race-activity-list-loading', onListLoadingOrUpdated);
            this._addEventListener(document, 'virtual-race-activity-list-updated', onListLoadingOrUpdated);
            ['updateGenderFilter', 'updateAgeFilter', 'updateTeamFilter', 'updateSearch', 'updateCurrentUnit'].forEach(eventName => {
                this._addEventListener(document, eventName, onListLoadingOrUpdated);
            });
        }

        _handleClick(e) {
            if (!this._state || this._isInitializing) return;
            if (this._state.isLoading) return;
            const th = e.target.closest('th.customTHClass');
            if (th) {
                const col = th.getAttribute('dataAttributr');
                const colIndexMap = { name: 0, lastRunningDate: 1, raceTime: 2, pace: 3, distance: 5, team: 6 };
                const idx = colIndexMap[col];
                if (col && idx !== undefined) {
                    this._state.setTable(col, e, idx).then(() => this._render());
                    this._render();
                }
                return;
            }
            const prev = e.target.closest('a.previous[data-pagelinks]');
            if (prev) {
                e.preventDefault();
                this._state.loadPreviousPage().then(() => this._render());
                this._render();
                return;
            }
            const next = e.target.closest('a.next[data-pagelinks]');
            if (next) {
                e.preventDefault();
                this._state.loadNextPage().then(() => this._render());
                this._render();
                return;
            }
            const pageLink = e.target.closest('a[data-pagelinks][data-index]');
            if (pageLink) {
                e.preventDefault();
                const idx = parseInt(pageLink.getAttribute('data-index'), 10);
                if (!isNaN(idx)) {
                    this._state.loadPageIndex(idx).then(() => this._render());
                    this._render();
                }
            }
        }

        _render() {
            if (!this._state || this._hasError) return;
            const tbody = this._$('#individual-virtual-race-time-table tbody');
            if (tbody) {
                tbody.classList.toggle('loading-row', this._isInitializing || this._state.isLoading);
            }

            if (this._isInitializing) return; // Don't update content while initializing
            const teamHeader = this._$('th[dataAttributr="team"]');
            if (teamHeader) {
                if (this._state.isteam) {
                    teamHeader.classList.remove('d-none');
                } else {
                    teamHeader.classList.add('d-none');
                }
            }
            const page = this._state.page || {};
            const current = this._state.currentPage || 1;
            const nav = this._$('.race-list-page');
            if (nav) {
                const isOnlyOnePage = current === 1 && page.has_next === false;
                console.log('Pagination check:', { current, has_next: page.has_next, has_previous: page.has_previous, isOnlyOnePage });
                
                if (isOnlyOnePage) {
                    nav.hidden = true;
                } else {
                    nav.hidden = !(page.has_next || page.has_previous);
                }

                const prevUl = nav.querySelector('ul li[data-next="previous"]')?.closest('ul');
                if (prevUl) prevUl.classList.toggle('d-none', !page.has_previous);

                const nextUl = nav.querySelector('ul li[data-next="next"]')?.closest('ul');
                if (nextUl) nextUl.classList.toggle('d-none', !page.has_next);

                const numbersContainer = nav.querySelector('.pagination-numbers');
                if (numbersContainer) {
                    const nextPage = page.next || 1;
                    const maxReached = this._state.maxPageReached || 1;
                    numbersContainer.innerHTML = Array.from({ length: nextPage }, (_, i) => i + 1)
                        .filter(i => i <= maxReached)
                        .map(i => `<li class="page-item" data-next="number"><a class="page-link ${i === current ? 'active' : ''}" data-index="${i}" data-pagelinks href="#" title="" aria-label="">${i}</a></li>`)
                        .join('');
                        requestAnimationFrame(() => {
                            numbersContainer.scrollLeft = numbersContainer.scrollWidth;
                        });
                }
            }
        }
    }

    if (!customElements.get('virtual-race-activity-list')) {
        customElements.define('virtual-race-activity-list', VirtualRaceActivityListElement);
    }

    // ============================================================================
    // CUSTOM ELEMENT: virtual-race-image-gallery
    // ============================================================================

    class VirtualRaceImageGalleryElement extends BaseCustomElement {
        static get observedAttributes() {
            return ['product-id'];
        }

        async _init() {
            const productId = this._getProductId();
            this._state = window.fetchImagegallery(productId);
            await this._state.getData();
        }

        _render() {
            if (!this._state) return;

            // Show content, hide loading
            this._toggle('[data-gallery-loading]', false);

            const container = this._$('[data-gallery-items]');
            if (!container) return;

            container.hidden = false;

            const items = this._state.items || [];
            const url = SecurityUtils.validateUrl;
            const esc = SecurityUtils.escapeHtml;
            const isSwiper = container.tagName === 'SWIPER-CONTAINER' || container.classList.contains('swiper-container');

            if (isSwiper) {
                container.innerHTML = items.map(item =>
                    `<swiper-slide class="swiper-slide" lazy="true"><div class="carousel-image-wrapper"><img src="${url(item.url) || '#'}" alt="${esc(item.alt)}" width="${item.width || 220}" height="${item.height || 220}" loading="lazy"></div></swiper-slide>`
                ).join('');
            } else {
                container.innerHTML = items.map(item =>
                    `<img src="${url(item.url) || '#'}" alt="${esc(item.alt)}" width="${item.width || 220}" height="${item.height || 220}" loading="lazy">`
                ).join('');
            }
        }
    }

    if (!customElements.get('virtual-race-image-gallery')) {
        customElements.define('virtual-race-image-gallery', VirtualRaceImageGalleryElement);
    }

    // ============================================================================
    // EXTERNAL EVENT HANDLERS (vanilla JS – no jQuery)
    // ============================================================================

    const unitToggle = document.getElementById('individual-virtual-race-time-unit-toggle');
    if (unitToggle) {
        unitToggle.addEventListener('click', function() {
            this.classList.toggle('miles');
            this.classList.toggle('km');
            const unit = this.getAttribute('data-active-unit');

            if (unit === 'miles') {
                document.dispatchEvent(new CustomEvent('updateCurrentUnit', { detail: { unit: 'km' } }));
                this.setAttribute('data-active-unit', 'km');
            } else {
                document.dispatchEvent(new CustomEvent('updateCurrentUnit', { detail: { unit: 'miles' } }));
                this.setAttribute('data-active-unit', 'miles');
            }

            const milesToggle = document.getElementById('miles-toggle');
            const kmToggle = document.getElementById('km-toggle');
            if (milesToggle) milesToggle.classList.toggle('active-unit');
            if (kmToggle) kmToggle.classList.toggle('active-unit');
        });
    }

    function initFilterHandlers() {
        const genderFilter = document.getElementById('individual-virtual-race-time-gender-filter');
        const ageFilter = document.getElementById('individual-virtual-race-time-age-filter');
        const teamFilter = document.getElementById('individual-virtual-race-time-team-filter');
        const searchInput = document.getElementById('individual-virtual-race-time-search');

        if (genderFilter) {
            genderFilter.addEventListener('change', function(e) {
                document.dispatchEvent(new CustomEvent('updateGenderFilter', {
                    detail: { gender: e.target.value }
                }));
            });
        }
        if (ageFilter) {
            ageFilter.addEventListener('change', function(e) {
                document.dispatchEvent(new CustomEvent('updateAgeFilter', {
                    detail: { age: e.target.value }
                }));
            });
        }
        if (teamFilter) {
            teamFilter.addEventListener('change', function(e) {
                document.dispatchEvent(new CustomEvent('updateTeamFilter', {
                    detail: { team: e.target.value }
                }));
            });
        }
        if (searchInput) {
            const debouncedSearch = Utility.debounce(function(e) {
                document.dispatchEvent(new CustomEvent('updateSearch', {
                    detail: { search: e.target.value }
                }));
            }, CONFIG.DEBOUNCE_DELAY);
            searchInput.addEventListener('input', debouncedSearch);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initFilterHandlers);
    } else {
        initFilterHandlers();
    }

    // Clear legacy session storage
    sessionStorage.removeItem('vr_sort_order');

})();
